import React, { memo, useEffect, useState, useMemo } from 'react';
import classNames from 'classnames';
import { where } from 'firebase/firestore';
import Card, { CardBody, CardHeader, CardLabel, CardTitle } from './bootstrap/Card';
import Money from '../helpers/utils/money/money';
import useCmvProducts from '../hooks/reports/useCmvProducts';
import Spinner from './bootstrap/Spinner';
import SalesDB from '../database/wrappers/sales';
import { PAYMENT_TYPE } from '../types/payment';

const TopProductItem = memo(({ name, totalCost, totalQtd, cmvPercent, index, courtesyQtd }) => {
    const rankColors = ['#FFD700', '#C0C0C0', '#CD7F32'];
    const rankColor = index < 3 ? rankColors[index] : '#6c757d';
    const getColorClasses = (percent) => {
        if (percent <= 30) {
            return { bar: 'bg-success', text: 'text-success' };
        }
        if (percent >= 31 && percent <= 38) {
            return { bar: 'bg-warning', text: 'text-warning' };
        }
        return { bar: 'bg-danger', text: 'text-danger' };
    };

    const { bar: barColor, text: textColor } = getColorClasses(cmvPercent);

    return (
        <div className='col-12'>
            <div className='d-flex align-items-center py-3 border-bottom'>
                <div className='me-3 fw-bold' style={{ fontSize: 20, width: 30, color: rankColor }}>
                    {index + 1}
                </div>
                <div className='flex-grow-1'>
                    <div className='fw-semibold'>{name}</div>
                    <div className='d-flex align-items-center mt-1'>
                        <div className='flex-grow-1 bg-light rounded-pill me-2' style={{ height: 8 }}>
                            <div
                                className={classNames('rounded-pill', barColor)}
                                style={{ width: `${cmvPercent > 100 ? 100 : cmvPercent}%`, height: '100%' }}
                            />
                        </div>
                        <small className={classNames('fw-bold', textColor)}>{cmvPercent.toFixed(1)}%</small>
                    </div>
                    <div className='text-muted 25 mt-1'>
                        QTD. vendida: {totalQtd}
                        {courtesyQtd > 0 && (
                            <span className='ms-2 text-info fw-bold'>
                                | QTD. cortesia: {courtesyQtd}
                            </span>
                        )}
                        <span className='ms-2'>
                            | Custo Total: {Money.centsToMaskMoney(totalCost)}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
});

const RankingPorProduto = ({ dataFilter }) => {
    const { rows, loading } = useCmvProducts(dataFilter);
    const [productsRank, setProductsRank] = useState([]);
    const [localSales, setLocalSales] = useState([]);

    useEffect(() => {
        if (!dataFilter.estab || !dataFilter.store || dataFilter.store === '#' || !dataFilter.firstDate || !dataFilter.secondDate) {
            setLocalSales([]);
            return;
        }

        const wheres = [
            where('storeId', '==', dataFilter.store),
            where('created_at', '>=', new Date(dataFilter.firstDate)),
            where('created_at', '<=', new Date(dataFilter.secondDate))
        ];

        const salesdb = new SalesDB(dataFilter.estab);

        const unsubscribe = salesdb.on(
            (fetchedSales) => {
                setLocalSales(fetchedSales);
            },
            ...wheres
        );

        return () => unsubscribe();
    }, [dataFilter]);

    const courtesyMap = useMemo(() => {
        const map = {};
        if (!localSales) return map;
        const courtesySales = localSales.filter(
            (sale) => sale.payment_type === PAYMENT_TYPE.COURTESY && !sale.reversed,
        );
        courtesySales.forEach((sale) => {
            if (sale.products && Array.isArray(sale.products)) {
                sale.products.forEach((product) => {
                    if (product.id && product.qtd) map[product.id] = (map[product.id] || 0) + product.qtd;
                    if (product.complements && Array.isArray(product.complements)) {
                        product.complements.forEach((complement) => {
                            if (complement.id && complement.qtd) map[complement.id] = (map[complement.id] || 0) + complement.qtd;
                        });
                    }
                });
            }
        });
        return map;
    }, [localSales]);

    useEffect(() => {
        if (rows) {
            const filteredRows = rows
                .filter(row => row.totalCost > 0 && row.total > 0)
                .map(row => {
                    const productId = row.idWithOutDate ? row.idWithOutDate.split('-')[0] : null;
                    return {
                        ...row,
                        cmvPercent: (row.totalCost / row.total) * 100,
                        courtesyQtd: productId ? courtesyMap[productId] || 0 : 0,
                    };
                });

            filteredRows.sort((a, b) => b.cmvPercent - a.cmvPercent);
            setProductsRank(filteredRows);
        }
    }, [rows, courtesyMap]);

    if (loading) {
        return (
            <div className='d-flex w-100 h-100 justify-content-center align-items-center'>
                <Spinner />
            </div>
        );
    }

    return (
        <CardBody isScrollable style={{ height: '330px' }}>
            <CardHeader>
                <CardLabel icon='Assessment' iconColor='primary'>
                    <CardTitle>Top Custos CMV</CardTitle>
                </CardLabel>
            </CardHeader>
            <div className='row g-1'>
                {productsRank.length === 0 ? (
                    <div className='col-12 text-center text-muted p-5'>
                        <p>Não há produtos com custo para exibir no ranking.</p>
                    </div>
                ) : (
                    productsRank.map((prod, index) => (
                        <TopProductItem key={prod.id} index={index} {...prod} />
                    ))
                )}
            </div>
        </CardBody>
    );
};

export default memo(RankingPorProduto);